import StompIt from 'stomp-it'
import { reverse, cloneDeep } from 'lodash'
import { client } from '../helpers/http'
import { isDeactivated } from '@/helpers/helpers'
import { EVENT, dispatchCustomEvent } from '@/helpers/customEvents'

class ChatApi {
  constructor (config) {
    this.settings = {
      baseUrl: config.baseUrl,
      wsEndpoint: '/ws',
      channel: config.channel,
      systemNotificationBaseUrl: config.systemNotificationBaseUrl,
      systemNotificationChannel: config.systemNotificationChannel
    }

    this.connectState = {
      user: null,
      conversations: null,
      conversationSubscriptions: []
    }

    this.chatStatus = {
      started: false,
      uuid: undefined,
      connected: false,
      eventHandlers: [],
      messages: undefined,
      msgIdCount: 1000
    }

    this.$stomp = new StompIt(this.settings.baseUrl + this.settings.wsEndpoint)
  }

  _dispatchEvent (event) {
    this.chatStatus.eventHandlers.forEach((handler) => {
      handler(event)
    })
  }

  _subscribeConversation (convId) {
    this.$stomp.subscribe('/channel/' + convId, (payload) => {
      let message = JSON.parse(payload.body)
      const stompMessageId = payload.headers['message-id']

      // new message notification
      if (message.type === 'CHAT') {
        client('ms').get('get-message', { messageId: message.msgId })
          .then(response => {
            if (response.data && response.data.msgText) { // prevent injection attacks
              response.data.msgText = response.data.msgText.replace('<', '&lt;').replace('>', '&gt;')
            }
            this._dispatchEvent({
              type: 'MESSAGE',
              payload: response.data
            })
          })
      }

      if (message.event === 'AGENT_STATUS_CHANGED') {
        this._dispatchEvent({
          type: message.event,
          payload: message.payload
        })
      }

      if (message.event === 'AGENT_JOINED_CONVERSATION') {
        this._dispatchEvent({
          type: message.event,
          payload: message.payload
        })
      }

      if (message.event === 'AGENT_LEFT_CONVERSATION') {
        this._dispatchEvent({
          type: message.event,
          payload: message.payload
        })
      }

      // new message notification (new unified style)
      if (message.event === 'MESSAGE') {
        // CHAT message
        if (message.payload.msgType === 'CHAT') {
          if (message.payload && message.payload.msgText) { // prevent injection attacks
            message.payload.msgText = message.payload.msgText.replace('<', '&lt;').replace('>', '&gt;')
          }
          this._dispatchEvent({
            type: 'MESSAGE',
            payload: message.payload
          })
        }
        if (message.payload.msgType === 'JOIN') {
          this._dispatchEvent({
            type: 'AGENT_JOINED_CONVERSATION',
            payload: message.payload
          })
        }
        if (message.payload.msgType === 'LEAVE') {
          this._dispatchEvent({
            type: 'AGENT_LEFT_CONVERSATION',
            payload: message.payload
          })
        }
        if (message.payload.msgType === 'HANDOVER') {
          this._dispatchEvent({
            type: 'CONVERSATION_HANDOVER',
            payload: message.payload
          })
        }
        if (message.payload.msgType === 'CHECK_ACTIVITY') {
          this._dispatchEvent({
            type: 'CHECK_ACTIVITY',
            payload: message.payload
          })
        }
      }
      if (
        message.event === 'AIAIBOT' &&
        ['message', 'session_ended'].includes(message.payload.eventType)
      ) {
        const {
          payloadType,
          payload
        } = message.payload.data.message
        
        this._dispatchEvent({
          type: 'MESSAGE',
          payload: {
            msgType: 'AIAIBOT',
            msgId: stompMessageId,
            payloadType,
            payload
          }
        })
      }
      if (message.event === 'AIAIBOT_INITIALIZED') {
        this._dispatchEvent({
          type: 'AIAIBOT_SESSION_INITIALIZED',
          payload: {
            sessionId: message.payload
          }
        })
      }
      if (message.event === 'AIAIBOT_INACTIVE_CLOSE') {
        this._dispatchEvent({
          type: 'AIAIBOT_INACTIVE_CLOSE',
          payload: message.payload
        })
      }
      if (message.event === 'AIAIBOT_NOT_INITIALIZED') {
        this._dispatchEvent({
          type: 'AIAIBOT_SESSION_NOT_INITIALIZED'
        })
      }
      if (message.event === 'AGENT_TYPING') {
        this._dispatchEvent({
          type: message.payload,
          payload: message.payload
        })
      }
      if (message.event === 'AGENT_NOT_TYPING') {
        this._dispatchEvent({
          type: message.payload,
          payload: message.payload
        })
      }
      if (message.event === 'CONVERSATION_ENDED') {
        this._dispatchEvent({
          type: 'CONVERSATION_ENDED',
          payload: message.payload
        })
      }
      if (message.event === 'CONVERSATION_ENDED_BY_AGENT') {
        this._dispatchEvent({
          type: 'CONVERSATION_ENDED_BY_AGENT',
          payload: message.payload
        })
      }
      if (message.event === 'CHATBOT') {
        if (message.payload.botStatus === 'REJECTED' || message.payload.botStatus === 'TO_KUBE') {
          this._dispatchEvent({
            type: 'MESSAGE',
            payload: message.payload
          })
          this._dispatchEvent({
            type: 'CHATBOT_ACTIVE',
            payload: {status: false}
          })
        } else if (message.payload.botStatus === 'CHATBOT_LEFT') {
          this._dispatchEvent({
            type: 'CHATBOT_LEFT',
            payload: message.payload
          })
          this._dispatchEvent({
            type: 'CHATBOT_ACTIVE',
            payload: {status: false}
          })
        } else if (message.payload.botStatus === 'FINISHED') {
          console.log('5 star rating')
        } else {
          this._dispatchEvent({
            type: 'CHATBOT_ACTIVE',
            payload: {status: true}
          })
          this._dispatchEvent({
            type: 'MESSAGE',
            payload: message.payload
          })
        }
      }

      if (message.event === 'ARCHIVE_INACTIVE_CONVERSATION') {
        this._dispatchEvent({
          type: 'ARCHIVE_INACTIVE_CONVERSATION',
          payload: message.payload
        })
      }
    })
  }

  panicMode () {
    this._dispatchEvent({
      type: 'BACKEND_DOWN'
    })
  }

  getChatAvailable (lang) {
    let result = {
      systemReachable: true,
      isOpeningHours: true,
      isAgentAvailable: true,
      timeUntilClosing: null,
      outsideServiceHoursMessage: null,
      error: null
    }
    return client('ms').get('can-i-chat', { language: lang, channel: this.settings.channel })
      .then(response => {
        result.reason = response.data.reason
        
        if (response.data.available === false) {
          if (
            response.data.reason === 'out_of_business_hours' ||
            response.data.reason === 'public_holiday'
          ) {
            result.isOpeningHours = false
            result.outsideServiceHoursMessage = response.data.message
          } else if (response.data.reason === 'no_operator_available') {
            result.isAgentAvailable = false
          } else {
            result.systemReachable = false
          }
        }
        result.timeUntilClosing = response.data.timeUntilClosing
        return result
      })
      .catch(error => {
        result.systemReachable = false
        return { ...result, error }
      })
  }

  async getUiTexts (lang) {
    const MAX_RETRIES = process.env.VUE_APP_LOAD_LABELS_MAX_RETRIES

    for (var i = 1; i <= MAX_RETRIES; i++) {
      try {
        const response = await client('ms').get('text-all', { language: lang })
        return response.data
      } catch (e) {
        if (i === MAX_RETRIES) {
          console.error(e)
          return {}
        }
      }
    }
  }

  registerEventHandler (handler) {
    this.chatStatus.eventHandlers.push(handler)
  }

  connect (lang, trigger, chatBotStoryId) {
    // index -> user-info
    // -> assigned-to-me
    // -> get messages
    // connect websocket + subscribe

    this._dispatchEvent({
      type: 'SET_DISABLE_SEND_BUTTON',
      payload: true
    })
    let result = {
      user: null,
      conversation: null
    }

    return this.$stomp.connect()
      .then(() => client('ms').get('user-info'))
      .then(response => {
        result.user = response.data
        this._dispatchEvent({
          type: 'SET_USER_PROFILE',
          payload: response.data
        })
        return client('ms').get('conversations-assignedtome')
      })
      .then(response => {
        if (response.data) { // TODO: filter out ENDUSER_ENDED - this should probably be done in backend
          response.data = response.data.filter(el => { return el.status !== 'ENDUSER_ENDED' })
        }
        if (response.data.length) {
          return response
        } else {
          let params = {
            userId: result.user.userId,
            language: lang,
            channel: this.settings.channel,
            ...(chatBotStoryId ? { chatBotStoryId } : {})
          }
          if (trigger) {
            params.trigger = trigger
          }
          return client('ms').get('conversations-start-new', params)
            .then(() => {
              return client('ms').get('conversations-assignedtome')
            }, e => {
              console.log('New conversation failed to start, enabling send button' + e)
              this._dispatchEvent({
                type: 'SET_DISABLE_SEND_BUTTON',
                payload: false
              })
            })
        }
      })
      .then(response => {
        this._dispatchEvent({
          type: 'SET_DISABLE_SEND_BUTTON',
          payload: false
        })

        if (response.data) { // TODO: filter out ENDUSER_ENDED - this should probably be done in backend
          response.data = response.data.filter(el => { return el.status !== 'ENDUSER_ENDED' })
        }

        // MVP -> one enduser can have only one conversation
        result.conversation = response.data[0]

        this.chatStatus.conversations = response.data
        if (result.conversation && result.conversation.chatBotStatus && result.conversation.chatBotStatus !== 'TO_KUBE') {
          this._dispatchEvent({
            type: 'CONNECT_CHATBOT_ACTIVE_CONVERSATION',
            payload: true
          })
        }

        return client('ms').follow(result.conversation, 'messages')
          .then(response => {
            result.conversation.messages = this.prepareMessages(response.data)

            // check if an agent has joined the conversation
            const movements = result.conversation.messages
              .filter(message => ['JOIN', 'LEAVE'].indexOf(message.msgType) >= 0)

            if (movements.length > 0) {
              let latestMovement = movements.pop()
              if (latestMovement.msgType === 'JOIN') {
                this._dispatchEvent({
                  type: 'AGENT_IS_IN_CONVERSATION',
                  payload: latestMovement
                })
              }
            }

            this._subscribeConversation(result.conversation.convId)
            return result
          })
      })
      .then(result => {
        if (this.$stomp) {
          let api = this
          this.$stomp.registerEventHandler((event) => {
            switch (event.type) {
              case 'CONNECTED':
                api._dispatchEvent({
                  type: 'CONNECTION_OK',
                  connection: 'ws'
                })
                break
              case 'RECONNECTING':
                api._dispatchEvent({
                  type: 'CONNECTION_ISSUE',
                  connection: 'ws'
                })
                break
              case 'RECONNECTED':
                api._dispatchEvent({
                  type: 'CONNECTION_OK',
                  connection: 'ws'
                })
                // TODO: reload conversation messages via REST to prevent lost messages
                api._dispatchEvent({
                  type: 'WS_RECONNECTED'
                })
                break
              case 'DISCONNECTED':
                api._dispatchEvent({
                  type: 'CONNECTION_ISSUE',
                  connection: 'ws'
                })
                break
              default:
                break
            }
          })
        }
        return cloneDeep(result)
      }).catch(e => console.log(e))
  }

  disconnect () {
    this.$stomp.state = 'DISCONNECTED' // prevent Stomp-it from reconnecting
    return this.$stomp.disconnect()
  }

  prepareMessages (messages) {
    messages = reverse(messages)
      .map(message => {
        if (message.msgType === 'JOIN') {
          message.msgText = message.agentName || message.screenName + ' joined the conversation'
        }
        if (message.msgType === 'LEAVE') {
          message.msgText = message.agentName || message.screenName + ' left the conversation'
        }
        if (message.msgText) { // prevent injection attacks
          message.msgText = message.msgText.replace('<', '&lt;').replace('>', '&gt;')
        }
        return message
      })

    // RC-1699 Workaround for incorrect ordering of chatbot messages
    if (messages[2] && messages[2].botStatus === 'CHATBOT_JOINED' && messages[1].botStatus === 'INITIALIZED') {
      let tmpMsg = messages[1]
      messages[1] = messages[2]
      messages[2] = tmpMsg
    }

    return messages
  }

  sendUrl ({ convId }, page, timestamp) {
    return client('ms').post(null, 'add-page-url', { url: page, created: timestamp }, {}, { conversationId: convId })
  }

  sendMessage (msg, conv) {
    dispatchCustomEvent(EVENT.SEND.KEEP_ALIVE)
    this.$stomp.send('/app/' + conv.convId + '/chat.sendMessage', msg)
  }

  checkSession (userId, convId) {
    return client('ms').get('user-info')
      .then(response => {
        if (response.data.userId !== userId) {
          return false
        } else {
          return client('ms').get('conversations-assignedtome')
            .then(response => {
              if (response.data) { // TODO: filter out ENDUSER_ENDED - this should probably be done in backend
                response.data = response.data.filter(el => { return el.status !== 'ENDUSER_ENDED' })
                if (response.data) {
                  this._dispatchEvent({
                    type: 'SET_HIDE_FAB',
                    payload: false
                  })
                }
              }
              if (!response.data.length || (response.data.map(c => c.convId).indexOf(convId) === -1)) {
                return false
              }
              return true
            })
        }
      }).catch(e => console.log(e))
  }

  getExistingConversation () {
    if (isDeactivated(window._sanChatConfig)) {
      return
    }

    let userId
    return client('ms').get('user-info')
      .then(response => {
        if (response.data && response.data.userId) {
          userId = response.data.userId
        }
        return client('ms').get('conversations-assignedtome')
      })
      .then(response => {
        if (response.data) { // TODO: filter out ENDUSER_ENDED - this should probably be done in backend
          response.data = response.data.filter(el => { return el.status !== 'ENDUSER_ENDED' })
        }
        if (response.data.length) {
          return { userId: userId, convId: response.data[0].convId }
        }
        return undefined
      }).catch(e => console.log(e))
  }

  getUserInfo () {
    return client('ms').get('user-info')
      .then(response => {
        if (response.data && response.data.userId) {
          return response.data
        } else {
          return undefined
        }
      }).catch(e => console.log(e))
  }

  getMyArchivedConversations () {
    return client('ms').get('my-archived-conversations')
      .then(response => {
        return response.data
      }).catch(e => console.log(e))
  }

  getArchivedConversationMessages (convId) {
    return client('ms').get('archived-conv-messages', { convId: convId })
      .then(response => {
        return response.data
      }).catch(e => console.log(e))
  }

  getMessages (convId) {
    return client('ms').get('get-message', { messageId: convId + '/50/0' })
    .then(response => {
      if (response.data && response.data.length) {
        return response.data
      } else {
        return undefined
      }
    }).catch(e => console.log(e))
  }

  suspendConversation (conversation) {
    return client('ms').post(conversation, 'suspend')
  }

  resumeConversation (conversation) {
    return client('ms').post(conversation, 'resume')
  }

  endConversation (conversation) {
    return client('ms').post(conversation, 'end')
  }

  sendTranscript (convId, locale, email) {
    return client('ms').post(null, 'protocol-send', { email: email, language: locale }, {}, { conversationId: convId })
  }

  sendRating (convId, payload) {
    return client('ms').post(null, 'conversations-rate', payload, {}, { conversationId: convId })
  }

  sendNpsRating (convId, payload) {
    return client('ms').post(null, 'conversations-nps-rate', payload, {}, { conversationId: convId })
  }

  sendBasket (convId, basket) {
    return client('ms').post(null, 'add-basket-info', basket, {}, { conversationId: convId })
  }

  sendDataTransferFlag (convId, payload) {
    return client('ms').put(null, 'data-transfer-flag', payload, {}, {conversationId: convId})
  }

  sendConversationEvent (convId, payload) {
    this.$stomp.send('/app/' + convId + '/chat.sendMessage', payload)
  }

  uploadFile (formData) {
    return client('ms').post(null, 'msg-upload-file', formData)
  }

  archiveConversation (convId) {
    return client('ms').put(null, 'archive', {}, {}, { conversationId: convId })
      .catch((err) => {
        console.log('call agent-end error:', err)
      })
  }

  getSanitasSystemNotifications (lang) {
    const url = `${this.settings.systemNotificationBaseUrl}.${lang}.${this.settings.systemNotificationChannel}.json`
    return client().get(url)
  }

  // endChat NOT YET in enduser MVP

  // submitRating

  // handle events:
  // messageReceived
  // fileReceived
  // chatEnded
  // agentStatusChanged
  //
  // special events
  // connectionLost

  // other stuff
  // isTyping
  // last seen (agent and user)
}

export default ChatApi
